import { AccountTree } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  TemplateNodeSchema,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import QuestionOptionBar from 'src/features/templateEdit/questionOptions/QuestionOptionBar'
import { type ColumnInputType } from '../model'
import { TableColumnResponseTypeSelect } from './TableColumnResponseTypeSelect'

type Props = {
  columnNode: TemplateNodeSchema
  onUpdateColumn: (patch: Partial<TemplateQuestion>) => void
  onChangeType: (responseType: ColumnInputType) => void
  updateColumnNode: (nodeId: number, changeNode: TemplateNodeSchema) => void
  showEditDeviateComment: (node: TemplateNodeSchema) => void
  /** 条件分岐を設定できる列のときに渡す。未指定なら条件分岐アイコンを出さない。 */
  onOpenLogic?: () => void
}

/** 選択中の列の設定パネル。質問文 + 回答形式 + オプションは通常質問と同じ QuestionOptionBar を共用する。 */
export const TableColumnSettings = ({
  columnNode,
  onUpdateColumn,
  onChangeType,
  updateColumnNode,
  showEditDeviateComment,
  onOpenLogic,
}: Props) => {
  const question = columnNode.question
  const responseType = question?.responseType
  // 条件分岐が紐づいている列は回答形式を変更できない(通常質問と同じ挙動)
  const hasLogic = columnNode.nodes.length > 0

  return (
    <Stack spacing={2} alignItems="flex-start" sx={{ width: '100%', p: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: '28px' }}>
        {question?.name?.trim() || '質問名未設定'}
      </Typography>

      {/* 質問文(左) → 条件分岐アイコン → 回答形式(右) を横並びにする。
          通常質問の並びに合わせている。折り返すと縦積みになるため固定。 */}
      <Stack
        direction="row"
        alignItems="flex-end"
        sx={{ width: '100%', columnGap: 3 }}
      >
        <Stack spacing={0.75} sx={{ flexGrow: 1, minWidth: 240 }}>
          <Typography
            component="label"
            htmlFor="table-column-question-name"
            variant="body2"
            fontWeight={700}
          >
            質問文
          </Typography>
          <TextField
            id="table-column-question-name"
            size="small"
            fullWidth
            placeholder=""
            slotProps={{ inputLabel: { shrink: true } }}
            value={question?.name ?? ''}
            onChange={event => onUpdateColumn({ name: event.target.value })}
          />
        </Stack>

        <Stack direction="row" alignItems="flex-end" spacing={0.5}>
          {onOpenLogic && (
            <Tooltip title="条件分岐を設定">
              <IconButton onClick={onOpenLogic} aria-label="条件分岐の設定">
                <AccountTree color="primary" />
              </IconButton>
            </Tooltip>
          )}

          <TableColumnResponseTypeSelect
            responseType={responseType}
            disabledReason={
              hasLogic
                ? '条件分岐が設定されているため、変更できません'
                : undefined
            }
            onChangeType={onChangeType}
            onUpdateColumn={onUpdateColumn}
          />
        </Stack>
      </Stack>

      <QuestionOptionBar
        node={columnNode}
        updateTemplateNode={updateColumnNode}
        showEditDeviateComment={showEditDeviateComment}
        isEmployeeCheck={false}
        canTrace={false}
        // 表の列ではテキストは1行のみ対応のため、フォーマットを選択させない
        canSelectTextFormat={false}
        // 表の列では選択肢の情報画像に対応しない
        canConfigureInformationImages={false}
      />
    </Stack>
  )
}
